import React from "react";
import { CONFIG } from "../config";

const TICK_COUNT = 72;

// Precompute tick geometry once with stable, unique keys (by angle index).
const TICKS = Array.from({ length: TICK_COUNT }, (_, i) => {
  const major = i % 6 === 0;
  const angle = (i / TICK_COUNT) * Math.PI * 2;
  const r1 = 196;
  const r2 = major ? 184 : 190;
  return {
    id: `tick-${i}`,
    major,
    x1: 200 + r1 * Math.cos(angle),
    y1: 200 + r1 * Math.sin(angle),
    x2: 200 + r2 * Math.cos(angle),
    y2: 200 + r2 * Math.sin(angle),
  };
});

// A premium, hand-crafted animated Platinum object.
// Precision-machined disc with a slow specular sheen sweep,
// a rotating tick-ring, and a single orbiting marker.
// Built entirely with CSS/SVG in the Platinum palette (no photography).
export default function HeroVisual() {
  return (
    <div className="relative aspect-square w-full max-w-[560px] mx-auto select-none">
      {/* soft controlled ambient highlight */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 46% 40%, rgba(218,220,217,0.12), rgba(8,9,9,0) 62%)",
        }}
      />

      <div
        className="hero-float relative h-full w-full flex items-center justify-center"
        style={{ animation: "soft-appear 1.1s cubic-bezier(0.22,1,0.36,1) both" }}
      >
        {/* Rotating rings are wrapped in a clipped layer so their square
            bounding boxes (which grow when rotated) never cause page overflow. */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Outer tick ring (rotating) */}
          <div className="spin-cw-120 absolute inset-[4%]">
            <svg viewBox="0 0 400 400" className="w-full h-full">
              <circle
                cx="200"
                cy="200"
                r="196"
                fill="none"
                stroke="rgba(240,241,238,0.10)"
                strokeWidth="1"
              />
              {TICKS.map((t) => (
                <line
                  key={t.id}
                  x1={t.x1}
                  y1={t.y1}
                  x2={t.x2}
                  y2={t.y2}
                  stroke={t.major ? "rgba(240,241,238,0.28)" : "rgba(240,241,238,0.12)"}
                  strokeWidth={t.major ? 1.2 : 0.8}
                />
              ))}
            </svg>
          </div>

          {/* Orbiting marker */}
          <div className="orbit-40 absolute inset-[3%] pointer-events-none">
            <div
              className="absolute left-1/2 top-0 -translate-x-1/2 w-[7px] h-[7px] rounded-full"
              style={{
                background: "#F0F1EE",
                boxShadow: "0 0 10px rgba(240,241,238,0.6)",
              }}
            />
          </div>

          {/* Thin inner ring (counter-rotating) */}
          <div className="spin-ccw-90 absolute inset-[14%]">
            <svg viewBox="0 0 400 400" className="w-full h-full">
              <circle
                cx="200"
                cy="200"
                r="198"
                fill="none"
                stroke="rgba(240,241,238,0.08)"
                strokeWidth="1"
                strokeDasharray="2 10"
              />
            </svg>
          </div>
        </div>

        {/* The machined Platinum disc */}
        <div className="relative w-[62%] aspect-square rounded-full">
          {/* base metal */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle at 36% 30%, #F0F1EE 0%, #DADCD9 20%, #B5BAB7 46%, #8B918D 72%, #5B615E 100%)",
              boxShadow:
                "inset 0 2px 6px rgba(255,255,255,0.35), inset 0 -14px 30px rgba(0,0,0,0.5), 0 30px 70px rgba(0,0,0,0.55)",
            }}
          />

          {/* concentric machined groove */}
          <div
            className="absolute inset-[9%] rounded-full"
            style={{
              boxShadow:
                "inset 0 1px 2px rgba(255,255,255,0.25), inset 0 -2px 4px rgba(0,0,0,0.35)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
          />

          {/* rotating specular sheen sweep */}
          <div className="absolute inset-0 rounded-full overflow-hidden">
            <div
              className="sheen-sweep absolute inset-[-25%]"
              style={{
                background:
                  "conic-gradient(from 0deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.28) 30deg, rgba(255,255,255,0) 78deg, rgba(255,255,255,0) 210deg, rgba(255,255,255,0.14) 250deg, rgba(255,255,255,0) 300deg)",
                mixBlendMode: "screen",
              }}
            />
          </div>

          {/* engraving */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span
              className="text-[10px] tracking-[0.42em] uppercase"
              style={{ color: "rgba(60,64,62,0.75)", textShadow: "0 1px 0 rgba(255,255,255,0.35)" }}
            >
              Platinum · Pt
            </span>
            <span
              className="mt-2 text-[40px] sm:text-[56px] font-semibold tracking-[0.04em]"
              style={{
                color: "rgba(74,79,76,0.9)",
                textShadow: "0 1px 1px rgba(255,255,255,0.4), 0 -1px 1px rgba(0,0,0,0.25)",
              }}
            >
              {CONFIG.token}
            </span>
            <span
              className="mt-2 text-[9px] tracking-[0.4em] uppercase"
              style={{ color: "rgba(60,64,62,0.7)", textShadow: "0 1px 0 rgba(255,255,255,0.3)" }}
            >
              1 {CONFIG.token} = 10 mg
            </span>
          </div>

          {/* top soft highlight */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 34% 24%, rgba(255,255,255,0.5), rgba(255,255,255,0) 34%)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
